import {
    CheckCircleFilled,
    ClockCircleOutlined,
    CopyOutlined,
    CrownOutlined,
    FireFilled,
    GiftOutlined,
    LoadingOutlined,
    LockOutlined,
    ReloadOutlined,
    SafetyOutlined,
    UnlockOutlined,
} from '@ant-design/icons';
import { Button, Drawer, Input, Modal, Spin, Tooltip, message } from 'antd';
import cx from 'classnames';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import PayQRCodes, { isAlipayRedirect } from '../../../../components/PayQRCodes';
import usePayOrder, { appliedRedeem, applyRedeem, revokeRedeem } from '../../../../components/PayQRCodes/usePayOrder';
import {
    clearPackageEntitlement,
    getSelectedPackageEntitlement,
} from '../../../../Service/packageEntitlements';
import {
    claimOffer,
    getAddonConfig,
    getChapterPrices,
    getOrderInfo,
    payChapter,
    payFull,
    payUpgrade,
    rememberOrder,
} from '../../api';
import {
    EXTRA_ROWS,
    addonKey,
    addonsFromQuote,
    copyText,
    formatYuan,
    portalContainer,
    visibleAddons,
} from './utils';
import styles from './PayDialog.module.scss';

let addonConfigCache = null;

const loadAddonConfig = async () => {
    if (addonConfigCache) return addonConfigCache;
    const data = await getAddonConfig();
    const items = Array.isArray(data?.items) ? data.items : Array.isArray(data) ? data : [];
    addonConfigCache = items;
    return items;
};

const errorText = (error, fallback) => error?.response?.data?.msg || error?.message || fallback;

/**
 * 付款单换了（改附加服务重新建单）时把优惠券挪过去：先从旧单撤回，再用于新单。
 * 没挂优惠券或付款单没变返回 null；挪失败提示用户重新输入，不影响生成付款码。
 */
async function carryCoupon(previous, orderNo) {
    if (!previous?.code || !orderNo || previous.orderNo === orderNo) return null;
    try {
        await revokeRedeem(previous.code);
        return await applyRedeem(orderNo, previous.code);
    } catch (error) {
        if (!error?.response) message.error(errorText(error, '优惠券转到新订单失败，请重新输入'));
        return null;
    }
}

/** 限时免费的附加服务（价格 0 且带划线原价） */
const isFreeAddon = (item) => !(Number(item?.price) > 0) && Number(item?.originalPrice) > 0;

/** 特价的附加服务（带高于现价的划线原价，目前是降AIGC） */
const isDiscountedAddon = (item) =>
    Number(item?.price) > 0 && Number(item?.originalPrice) > Number(item?.price);

const pad2 = (n) => String(n).padStart(2, '0');

const DAY_MS = 24 * 60 * 60 * 1000;

/** 特惠截止时间只是营销上的「限时」：过了就按天顺延，倒计时第二天重新开始，价格不变 */
export const rollDaily = (endsAt) => {
    let value = Number(endsAt);
    if (!Number.isFinite(value) || value <= 0) return null;
    const now = Date.now();
    if (value <= now) value += Math.ceil((now - value + 1) / DAY_MS) * DAY_MS;
    return value;
};

/** 剩余毫秒 → 倒计时文案：HH:MM:SS，超过一天带「x天」，到点后停在 00:00:00；short 时不足一小时只显示 MM:SS */
export const formatCountdown = (ms, short = false) => {
    const total = Math.max(0, Math.floor((Number(ms) || 0) / 1000));
    const days = Math.floor(total / 86400);
    const hours = Math.floor((total % 86400) / 3600);
    const minutes = Math.floor((total % 3600) / 60);
    const seconds = total % 60;
    if (short && days === 0 && hours === 0) return `${pad2(minutes)}:${pad2(seconds)}`;
    const clock = `${pad2(hours)}:${pad2(minutes)}:${pad2(seconds)}`;
    return days > 0 ? `${days}天 ${clock}` : clock;
};

/** 限时特惠倒计时：每秒刷新到 endsAt（毫秒时间戳）的剩余时间；没有截止时间返回 '' */
function useCountdown(endsAt, short = false) {
    const [now, setNow] = useState(() => Date.now());
    useEffect(() => {
        if (!endsAt) return undefined;
        setNow(Date.now());
        const timer = setInterval(() => setNow(Date.now()), 1000);
        return () => clearInterval(timer);
    }, [endsAt]);
    return endsAt ? formatCountdown(endsAt - now, short) : '';
}

const qrSize = () => (typeof window !== 'undefined' && window.innerWidth < 1100 ? 112 : 120);

/** 手机（窄屏）用底部弹层，电脑用居中弹窗 */
const MOBILE_QUERY = '(max-width: 767px)';

/** 右下角客服浮钮（antd FloatButton）的 z-index，弹窗 / 弹层要压在它上面，底部的支付按钮才不会被挡 */
const FLOAT_BUTTON_Z = 1200;

const matchMobile = () =>
    typeof window !== 'undefined' &&
    typeof window.matchMedia === 'function' &&
    window.matchMedia(MOBILE_QUERY).matches;

const useIsMobile = () => {
    const [mobile, setMobile] = useState(matchMobile);
    useEffect(() => {
        if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return undefined;
        const query = window.matchMedia(MOBILE_QUERY);
        const onChange = (event) => setMobile(event.matches);
        if (query.addEventListener) query.addEventListener('change', onChange);
        else query.addListener(onChange);
        return () => {
            if (query.removeEventListener) query.removeEventListener('change', onChange);
            else query.removeListener(onChange);
        };
    }, []);
    return mobile;
};

/** 附加服务里标「热门」的项：第一项和降 AI 率（与墨得问题一致） */
const isHotAddon = (item, index) => index === 0 || item.serviceType === 'humanize';

const PLAN_LABEL = { full: '全篇解锁', single: '单章解锁', upgrade: '补差价解锁全文' };

const BENEFITS = {
    full: [
        '完整正文 + 参考文献，图表 / 公式一并生成',
        '生成完成后可下载 Word 文档',
        '无需注册登录，凭订单号查看进度与预览',
        '本机自动记住订单，换设备凭订单号查询',
    ],
    single: [
        '解锁本章完整正文，含图表 / 公式',
        '之后可补差价解锁全文，不重复收费',
        '无需注册登录，凭订单号查看进度',
    ],
    upgrade: [
        '付款后自动生成其余章节',
        '已解锁的章节不会重复收费',
        '生成完成后可下载完整 Word',
    ],
};

/**
 * 写作订单解锁付款弹窗（预览页、订单结果页共用），观感参照墨得问题的解锁弹窗：
 * 电脑居中弹窗（左：方案 / 附加服务 / 价格明细 / 权益，右：金额 + 双码 + 兑换码），手机底部弹层（金额和支付宝按钮吸底）。
 * mode='unlock'：全篇解锁 / 单章解锁两个方案；mode='upgrade'：单章后补差价解锁全文。
 * 付款走灵感风暴现有现金支付：拿到待支付订单号 → 支付宝 / 微信双码（手机：支付宝 H5）+ 兑换码（套餐）。
 *
 * @param {object} props
 * @param {boolean} props.open
 * @param {() => void} props.onClose
 * @param {string} props.uid 写作订单 uid（quote 存在时以 quote.uid 为准）
 * @param {object} [props.quote] OrderQuote；不传时按 uid 读 /ai/order/info 里的报价
 * @param {string} [props.title] 记录到本机订单列表的标题
 * @param {string} [props.queryType] PAPER / OPENING_REPORT / LITERATURE_REVIEW（决定隐藏哪个附加服务）
 * @param {'full'|'single'} [props.initialPlan]
 * @param {string} [props.initialChapter] 单章方案预选的章标题
 * @param {Array} [props.chapterPrices] 已拉到的章节价格（弹窗打开时仍会刷新一次）
 * @param {(selected: object) => Promise<object>} [props.recreate] 改附加服务时重新建单，resolve 新报价；不传则不能改附加服务
 * @param {'unlock'|'upgrade'} [props.mode]
 * @param {number} [props.upgradePrice] 补差价金额（展示用，以下单返回为准）
 * @param {(orderNo: string, ctx: {uid: string}) => void} [props.onPaid] 不传时记住订单并跳转 /order/result
 */
export default function WritingPayModal({
    open,
    onClose,
    uid,
    quote: quoteProp,
    title,
    queryType,
    initialPlan = 'full',
    initialChapter = null,
    chapterPrices: chapterPricesProp,
    recreate,
    mode = 'unlock',
    upgradePrice,
    onPaid,
    zIndex = 1050,
}) {
    const navigate = useNavigate();
    const isMobile = useIsMobile();
    const layer = Math.max(zIndex, FLOAT_BUTTON_Z + 1);
    const isUpgrade = mode === 'upgrade';
    const [plan, setPlan] = useState(isUpgrade ? 'upgrade' : initialPlan);
    const [chapter, setChapter] = useState(initialChapter);
    const [fetchedQuote, setFetchedQuote] = useState(null);
    const quote = quoteProp || fetchedQuote;
    const effectiveUid = quote?.uid || uid;
    const [addonItems, setAddonItems] = useState(() => addonConfigCache || []);
    const [selected, setSelected] = useState(() => addonsFromQuote(quote));
    const [recreating, setRecreating] = useState(false);
    const [chapterList, setChapterList] = useState(null);
    const [chapterLoading, setChapterLoading] = useState(false);
    const [payInfo, setPayInfo] = useState(null);
    const [preparing, setPreparing] = useState(false);
    const [prepareError, setPrepareError] = useState('');
    const [prepareSeq, setPrepareSeq] = useState(0);
    const [code, setCode] = useState('');
    const [redeeming, setRedeeming] = useState(false);
    /** 本付款单上挂着的优惠券抵扣：{ orderNo, code, discountAmount, payableAmount, orderAmount } */
    const [discount, setDiscount] = useState(null);
    /** 优惠券挂在别的未支付订单上：{ code, previousOrderNo } */
    const [revokePrompt, setRevokePrompt] = useState(null);
    /** 关弹窗挽留领到的限时 88 折 { uid, price, rate, expiresAt }；报价里也可能带着（刷新 / 重开弹窗时恢复） */
    const [offer, setOffer] = useState(null);
    const [retention, setRetention] = useState(false);
    const [claiming, setClaiming] = useState(false);
    const retentionShownRef = useRef(new Set());
    const offerWasActiveRef = useRef(false);
    const offerExpiredRef = useRef(0);
    const [size, setSize] = useState(qrSize);
    const [selectedPackage, setSelectedPackage] = useState(null);

    const paidRef = useRef(false);
    const payReqRef = useRef(0);
    const recreateReqRef = useRef(0);
    const latest = useRef({});
    latest.current = {
        onPaid,
        onClose,
        uid: effectiveUid,
        title: title || quote?.paperTitle,
        queryType: quote?.queryType || queryType,
        quote,
        selected,
        discount,
    };

    const handlePaid = useCallback(
        (orderNo) => {
            if (paidRef.current || !orderNo) return;
            paidRef.current = true;
            const ctx = latest.current;
            message.success('支付成功，全文已开始生成');
            // 付款 / 兑换 / 单章解锁 / 补差价成功：记到首页「我的生成记录」（已付款）
            rememberOrder({
                no: orderNo,
                uid: ctx.uid,
                title: ctx.title,
                kind: 'WRITING',
                queryType: ctx.queryType,
                status: 'PAID',
            });
            if (ctx.onPaid) {
                ctx.onPaid(orderNo, { uid: ctx.uid });
                return;
            }
            ctx.onClose?.();
            navigate(`/order/result?no=${encodeURIComponent(orderNo)}`);
        },
        [navigate],
    );
    latest.current.handlePaid = handlePaid;

    const { channels, start, retry, reset, stop } = usePayOrder({ onPaid: handlePaid });

    // 每次打开：按入口重置方案、预选章节、兑换码
    useEffect(() => {
        if (!open) return;
        const pkg = getSelectedPackageEntitlement();
        setPlan(isUpgrade ? 'upgrade' : initialPlan);
        setChapter(initialChapter);
        setCode('');
        setDiscount(null);
        setRevokePrompt(null);
        setSize(qrSize());
        setSelectedPackage(pkg);
        paidRef.current = false;
        // 只在打开瞬间读取入口参数（initialPlan / initialChapter 之后的变化不覆盖用户的选择）
    }, [open]);

    // 报价变化（含重新建单成功）时同步已选附加服务
    const quoteKey = addonKey(addonsFromQuote(quote));
    useEffect(() => {
        setSelected(addonsFromQuote(latest.current.quote));
    }, [quote?.uid, quoteKey]);

    // 没传报价时读订单详情里的报价（订单结果页「去支付」）
    useEffect(() => {
        if (!open || quoteProp || !uid || isUpgrade) return undefined;
        let alive = true;
        getOrderInfo(uid)
            .then((info) => alive && setFetchedQuote(info?.quote || null))
            .catch(() => {});
        return () => {
            alive = false;
        };
    }, [open, quoteProp, uid, isUpgrade]);

    useEffect(() => {
        if (!open || isUpgrade || !recreate) return;
        loadAddonConfig()
            .then(setAddonItems)
            .catch(() => {});
    }, [open, isUpgrade, recreate]);

    // 单章方案：打开时刷新一次章节价格（已买过单章 / 已解锁等状态以最新为准）
    useEffect(() => {
        if (!open || plan !== 'single' || !effectiveUid) return undefined;
        let alive = true;
        setChapterLoading(true);
        getChapterPrices(effectiveUid)
            .then((list) => alive && setChapterList(Array.isArray(list) ? list : []))
            .catch(() => alive && setChapterList((prev) => prev || []))
            .finally(() => alive && setChapterLoading(false));
        return () => {
            alive = false;
        };
    }, [open, plan, effectiveUid]);

    const chapters = chapterList || (Array.isArray(chapterPricesProp) ? chapterPricesProp : null);
    const chapterInfo =
        plan === 'single' && chapter
            ? (chapters || []).find((item) => item.chapterTitle === chapter) || null
            : null;
    const canPayChapter =
        !!chapterInfo && !chapterInfo.unlocked && chapterInfo.unlockable !== false;

    const addonOptions = useMemo(
        () => visibleAddons(addonItems, quote?.queryType || queryType),
        [addonItems, quote?.queryType, queryType],
    );

    // 改附加服务：停顿 400ms 再重新建单，连点多个只建一次
    const selectedKey = addonKey(selected);
    const pendingAddons = !!recreate && plan === 'full' && selectedKey !== quoteKey;
    useEffect(() => {
        if (!open || !pendingAddons) return undefined;
        const timer = setTimeout(async () => {
            const id = ++recreateReqRef.current;
            setRecreating(true);
            try {
                await recreate({ ...latest.current.selected });
            } catch (error) {
                if (id === recreateReqRef.current) {
                    setSelected(addonsFromQuote(latest.current.quote));
                    if (!error?.response) message.error(errorText(error, '更新订单失败，请重试'));
                }
            } finally {
                if (id === recreateReqRef.current) setRecreating(false);
            }
        }, 400);
        return () => clearTimeout(timer);
    }, [open, pendingAddons, selectedKey, recreate]);

    const ready =
        open &&
        !!effectiveUid &&
        !recreating &&
        !pendingAddons &&
        (plan === 'full' || plan === 'upgrade' || canPayChapter);

    // 生成待支付订单号 → 双通道付款码 + 轮询到账
    useEffect(() => {
        const id = ++payReqRef.current;
        reset();
        setPayInfo(null);
        setPrepareError('');
        if (!ready) {
            setPreparing(false);
            return undefined;
        }
        setPreparing(true);
        const timer = setTimeout(async () => {
            try {
                let res;
                if (plan === 'upgrade') res = await payUpgrade(effectiveUid);
                else if (plan === 'single') res = await payChapter(effectiveUid, chapter);
                else res = await payFull(effectiveUid);
                if (id !== payReqRef.current) return;
                if (!res?.orderNo) throw new Error('获取付款订单失败，请重试');
                // 改附加服务后换了付款单：把上一单挂着的优惠券挪到新单上（先撤回，再用于新单）
                const previous = latest.current.discount;
                const moved = await carryCoupon(previous, res.orderNo);
                if (id !== payReqRef.current) return;
                setPayInfo(res);
                setPreparing(false);
                if (moved?.result === 'PAID') {
                    latest.current.handlePaid(res.orderNo);
                    return;
                }
                if (moved?.result === 'DISCOUNTED') setDiscount({ ...moved, orderNo: res.orderNo });
                else if (previous && previous.orderNo !== res.orderNo) setDiscount(null);
                start(res.orderNo);
            } catch (error) {
                if (id !== payReqRef.current) return;
                setPreparing(false);
                setPrepareError(errorText(error, '获取付款订单失败，请重试'));
            }
        }, 250);
        return () => clearTimeout(timer);
    }, [ready, plan, effectiveUid, chapter, prepareSeq, reset, start]);

    const toggleAddon = (serviceType) => {
        if (recreating) return;
        setSelected((prev) => ({ ...prev, [serviceType]: !prev[serviceType] }));
    };

    // 付款单准备好后查一下本单是否已挂着优惠券（关掉弹窗再打开、刷新后回来）
    const payOrderNo = payInfo?.orderNo;
    const payPrice = payInfo?.price;
    useEffect(() => {
        if (!payOrderNo) return undefined;
        let alive = true;
        Promise.resolve(appliedRedeem(payOrderNo))
            .then((info) => {
                if (!alive || !info || info.result !== 'DISCOUNTED') return;
                setDiscount({ ...info, orderNo: payOrderNo });
            })
            .catch(() => {});
        return () => {
            alive = false;
        };
    }, [payOrderNo, payPrice]);

    /** 兑换结果：整单兑换到账 / 抵扣 / 要先撤回 */
    const applyResult = (orderNo, value, result) => {
        if (result.result === 'PAID') {
            stop();
            if (getSelectedPackageEntitlement()?.redeemCode === value) clearPackageEntitlement();
            handlePaid(orderNo);
            return;
        }
        if (result.result === 'DISCOUNTED') {
            setDiscount({ ...result, orderNo });
            setRevokePrompt(null);
            setCode('');
            message.success(`已抵扣 ¥${formatYuan(result.discountAmount)}，还需支付 ¥${formatYuan(result.payableAmount)}`);
            // 金额变了，重新生成付款码
            start(orderNo);
            return;
        }
        if (result.result === 'NEED_REVOKE') {
            setRevokePrompt({ code: value, previousOrderNo: result.previousOrderNo });
        }
    };

    const handleRedeem = async () => {
        const value = code.trim();
        if (!value) {
            message.warning('请输入兑换码或套餐码');
            return;
        }
        if (!payInfo?.orderNo) {
            message.info('付款订单准备中，请稍候再试');
            return;
        }
        setRedeeming(true);
        try {
            const result = await applyRedeem(payInfo.orderNo, value);
            applyResult(payInfo.orderNo, value, result);
        } catch (error) {
            if (!error?.response) message.error(errorText(error, '兑换失败，请检查兑换码'));
        } finally {
            setRedeeming(false);
        }
    };

    /** 撤回优惠券：reapply=true 表示撤回后立刻用于本单（挂在别的订单上的情况） */
    const handleRevoke = async (value, reapply) => {
        if (!payInfo?.orderNo) return;
        setRedeeming(true);
        try {
            await revokeRedeem(value);
            if (reapply) {
                const result = await applyRedeem(payInfo.orderNo, value);
                applyResult(payInfo.orderNo, value, result);
            } else {
                setDiscount(null);
                message.success('已撤回优惠券，金额已恢复');
                start(payInfo.orderNo);
            }
        } catch (error) {
            if (!error?.response) message.error(errorText(error, '撤回失败，请稍后重试'));
        } finally {
            setRedeeming(false);
        }
    };

    // 限时 88 折：本次领到的优先，否则用报价里带的；到点即失效
    const quoteOffer =
        quote?.offerExpiresAt && Number(quote?.offerPrice) > 0
            ? {
                  uid: quote.uid,
                  price: Number(quote.offerPrice),
                  rate: Number(quote.offerRate) || 0.88,
                  expiresAt: Number(quote.offerExpiresAt),
              }
            : null;
    const offerSource = offer && offer.uid === effectiveUid ? offer : quoteOffer;
    const offerCountdown = useCountdown(offerSource ? offerSource.expiresAt : null, true);
    const activeOffer = offerSource && offerSource.expiresAt > Date.now() ? offerSource : null;
    const offerPercent = activeOffer ? Math.round(activeOffer.rate * 100) : 88;
    if (activeOffer) offerWasActiveRef.current = true;
    const listPrice = activeOffer ? activeOffer.price : quote?.totalPrice;

    // 倒计时到点：优惠失效，重新取付款单（回到原价）
    useEffect(() => {
        if (!offerSource || offerSource.expiresAt > Date.now() || !offerWasActiveRef.current) return;
        if (offerExpiredRef.current === offerSource.expiresAt) return;
        offerExpiredRef.current = offerSource.expiresAt;
        offerWasActiveRef.current = false;
        setOffer(null);
        if (open && !paidRef.current) {
            message.info('限时优惠已结束，已恢复原价');
            setPrepareSeq((n) => n + 1);
        }
    }, [offerSource, offerCountdown, open]);

    /** 关闭：长文 / 综述 / 开题的未解锁订单第一次关闭先挽留一下（限时 88 折），其余直接关 */
    const handleClose = () => {
        if (retention) {
            setRetention(false);
            onClose?.();
            return;
        }
        const canOffer =
            !isUpgrade &&
            !paidRef.current &&
            !!effectiveUid &&
            !!quote?.offerEligible &&
            !activeOffer &&
            !retentionShownRef.current.has(effectiveUid);
        if (canOffer) {
            retentionShownRef.current.add(effectiveUid);
            setRetention(true);
            return;
        }
        onClose?.();
    };

    /** 领取限时 88 折：拿到折后报价后重新取付款单、重新生成付款码 */
    const handleClaimOffer = async () => {
        if (!effectiveUid) return;
        setClaiming(true);
        try {
            const next = await claimOffer(effectiveUid);
            setRetention(false);
            if (next?.offerExpiresAt && Number(next.offerPrice) > 0) {
                offerExpiredRef.current = 0;
                setOffer({
                    uid: effectiveUid,
                    price: Number(next.offerPrice),
                    rate: Number(next.offerRate) || 0.88,
                    expiresAt: Number(next.offerExpiresAt),
                });
                setPlan('full');
                message.success(`已享 ${Math.round((Number(next.offerRate) || 0.88) * 100)} 折，5 分钟内付款有效`);
                setPrepareSeq((n) => n + 1);
            } else {
                message.info('优惠已失效');
            }
        } catch (error) {
            if (!error?.response) message.error(errorText(error, '领取优惠失败，请重试'));
        } finally {
            setClaiming(false);
        }
    };

    const busy = preparing || recreating || pendingAddons;
    const waitingChapter = plan === 'single' && !canPayChapter;
    let estimate = listPrice;
    if (plan === 'single') estimate = chapterInfo?.price;
    if (plan === 'upgrade') estimate = upgradePrice;
    const baseAmount = !busy && payInfo?.price != null ? payInfo.price : estimate;
    const activeDiscount = discount && payInfo?.orderNo && discount.orderNo === payInfo.orderNo ? discount : null;
    const amount = activeDiscount && !busy ? activeDiscount.payableAmount : baseAmount;
    // 价格明细：挂了优惠券时多一行抵扣，合计按抵扣后金额显示
    const discountTotal = (value) => (activeDiscount ? activeDiscount.payableAmount : value);
    const renderDiscountRow = () =>
        activeDiscount ? (
            <div className={styles.row}>
                <span>优惠券抵扣</span>
                <b className={styles.free}>-¥{formatYuan(activeDiscount.discountAmount)}</b>
            </div>
        ) : null;
    const amountText = amount != null ? `¥${formatYuan(amount)}` : '--';
    const words = Number(quote?.totalWords);
    const wordsText =
        Number.isFinite(words) && words > 0 ? `约 ${words.toLocaleString('zh-CN')} 字` : '';
    const addonServices = Array.isArray(quote?.addonServices) ? quote.addonServices : [];
    const isAcademic = quote?.model === 'academic';
    // 限时特惠：后端给了高于现价的划线原价才展示；默认赠送的图 / 表（开题报告 1 图 1 表）标「赠送」
    const originalBase =
        Number(quote?.originalBasePrice) > Number(quote?.basePrice) ? Number(quote.originalBasePrice) : null;
    const promoActive = plan === 'full' && !isUpgrade && originalBase != null;
    const countdown = useCountdown(promoActive ? rollDaily(quote?.promoEndsAt) : null);
    const freeFigures = Number(quote?.freeFigureCount) || 0;
    const freeTables = Number(quote?.freeTableCount) || 0;
    const dialogTitle = isUpgrade ? '补差价解锁全文' : '解锁全文';
    const subtitle = '免登录扫码支付，付款成功后自动开始生成';

    /** 金额下方一行说明：方案 + 字数 / 章名 / 附加服务数 */
    const amountNote = () => {
        if (plan === 'single')
            return chapterInfo ? `单章解锁 · ${chapterInfo.chapterTitle}` : '单章解锁 · 请先选择章节';
        if (plan === 'upgrade') return '补齐全文差价 · 已解锁章节不重复收费';
        const parts = ['全篇解锁'];
        if (wordsText) parts.push(wordsText);
        if (addonServices.length) parts.push(`含 ${addonServices.length} 项附加服务`);
        return parts.join(' · ');
    };

    // ---------- 左栏：方案 ----------

    const renderPlanTabs = () => (
        <div className={styles.plans} role="tablist">
            {[
                {
                    key: 'full',
                    label: '全篇解锁',
                    sub: '完整正文 + 参考文献，可加购附加服务',
                    badge: '推荐',
                    icon: <CrownOutlined />,
                },
                {
                    key: 'single',
                    label: '单章解锁',
                    sub: '先买一章看效果，之后可补差价',
                    icon: <UnlockOutlined />,
                },
            ].map((item) => (
                <button
                    key={item.key}
                    type="button"
                    role="tab"
                    aria-selected={plan === item.key}
                    className={cx(styles.plan, plan === item.key && styles.planActive)}
                    onClick={() => setPlan(item.key)}
                >
                    <span className={styles.planHead}>
                        <span className={styles.planIcon}>{item.icon}</span>
                        <span className={styles.planLabel}>{item.label}</span>
                        {item.badge ? <span className={styles.planBadge}>{item.badge}</span> : null}
                    </span>
                    <span className={styles.planSub}>{item.sub}</span>
                </button>
            ))}
        </div>
    );

    const renderSectionTitle = (text, tag, hint) => (
        <div className={styles.sectionTitle}>
            {text}
            {tag ? <span className={styles.sectionTag}>{tag}</span> : null}
            {hint ? <span className={styles.sectionHint}>{hint}</span> : null}
        </div>
    );

    const renderAddons = () => {
        if (!recreate || addonOptions.length === 0) return null;
        return (
            <section className={styles.section}>
                {renderSectionTitle('附加服务', '可选 · 一起生成')}
                <div className={styles.addons}>
                    {addonOptions.map((item, index) => {
                        const active = !!selected[item.serviceType];
                        const button = (
                            <button
                                key={item.serviceType}
                                type="button"
                                aria-pressed={active}
                                disabled={recreating}
                                className={cx(styles.addon, active && styles.addonActive)}
                                onClick={() => toggleAddon(item.serviceType)}
                            >
                                <span className={styles.addonHead}>
                                    <span className={styles.addonLabel}>
                                        {item.label}
                                        {isFreeAddon(item) ? (
                                            <span className={styles.freeTag}>限时免费</span>
                                        ) : isDiscountedAddon(item) ? (
                                            <span className={styles.hot}>特价</span>
                                        ) : isHotAddon(item, index) ? (
                                            <span className={styles.hot}>HOT</span>
                                        ) : null}
                                    </span>
                                    <span className={styles.check}>
                                        {active ? <CheckCircleFilled /> : null}
                                    </span>
                                </span>
                                {isFreeAddon(item) ? (
                                    <b className={cx(styles.addonPrice, styles.free)}>
                                        <s className={styles.original}>¥{formatYuan(item.originalPrice)}</s>免费
                                    </b>
                                ) : isDiscountedAddon(item) ? (
                                    <b className={styles.addonPrice}>
                                        <s className={styles.original}>¥{formatYuan(item.originalPrice)}</s>¥
                                        {formatYuan(item.price)}
                                    </b>
                                ) : (
                                    <b className={styles.addonPrice}>¥{formatYuan(item.price)}</b>
                                )}
                            </button>
                        );
                        return item.desc ? (
                            <Tooltip key={item.serviceType} title={item.desc} zIndex={layer + 10}>
                                {button}
                            </Tooltip>
                        ) : (
                            button
                        );
                    })}
                </div>
            </section>
        );
    };

    const renderFullSummary = () => {
        const extras = EXTRA_ROWS.filter(([countKey]) => Number(quote?.[countKey]) > 0);
        return (
            <section className={styles.section}>
                {renderSectionTitle('价格明细')}
                <Spin spinning={recreating || pendingAddons} tip="价格更新中" size="small">
                    <div className={styles.summary}>
                        <div className={styles.row}>
                            <span>
                                正文价格{wordsText ? `（${wordsText}）` : ''}
                                {isAcademic ? <span className={styles.modelTag}>特调模型</span> : null}
                            </span>
                            <b>
                                {originalBase ? (
                                    <s className={styles.original}>¥{formatYuan(originalBase)}</s>
                                ) : null}
                                ¥{formatYuan(quote?.basePrice)}
                            </b>
                        </div>
                        {extras.map(([countKey, priceKey, label]) => {
                            const gifted =
                                countKey === 'figureCount' ? freeFigures : countKey === 'tableCount' ? freeTables : 0;
                            const count = Number(quote[countKey]);
                            const free = quote?.extrasFree || !(Number(quote?.[priceKey]) > 0);
                            return (
                                <div className={styles.row} key={countKey}>
                                    <span>
                                        {label} × {count}
                                        {gifted > 0 && count > gifted ? (
                                            <span className={styles.gift}>赠 {gifted}</span>
                                        ) : null}
                                    </span>
                                    {free ? (
                                        <b className={styles.free}>{gifted > 0 && count <= gifted ? '赠送' : '暂免费'}</b>
                                    ) : (
                                        <b>¥{formatYuan(quote[priceKey])}</b>
                                    )}
                                </div>
                            );
                        })}
                        {addonServices.map((item) => (
                            <div className={styles.row} key={item.serviceType || item.label}>
                                <span>附加 · {item.label}</span>
                                {isFreeAddon(item) ? (
                                    <b className={styles.free}>
                                        <s className={styles.original}>¥{formatYuan(item.originalPrice)}</s>限时免费
                                    </b>
                                ) : isDiscountedAddon(item) ? (
                                    <b className={styles.accent}>
                                        <s className={styles.original}>¥{formatYuan(item.originalPrice)}</s>¥
                                        {formatYuan(item.price)}
                                    </b>
                                ) : (
                                    <b className={styles.accent}>¥{formatYuan(item.price)}</b>
                                )}
                            </div>
                        ))}
                        {activeOffer ? (
                            <div className={styles.row}>
                                <span>
                                    限时 {offerPercent} 折
                                    <span className={styles.gift}>{offerCountdown} 内付款有效</span>
                                </span>
                                <b className={styles.free}>
                                    -¥{formatYuan(Number(quote?.totalPrice) - activeOffer.price)}
                                </b>
                            </div>
                        ) : null}
                        {renderDiscountRow()}
                        <div className={styles.total}>
                            <span>合计</span>
                            <b>¥{formatYuan(discountTotal(listPrice))}</b>
                        </div>
                    </div>
                </Spin>
            </section>
        );
    };

    const renderSinglePlan = () => {
        const list = chapters || [];
        const lockedItems = list.filter((item) => !item.unlocked);
        const blocked =
            lockedItems.length > 0 && lockedItems.every((item) => item.unlockable === false);
        const reasons = Array.from(
            new Set(lockedItems.map((item) => item.reason).filter(Boolean)),
        );
        const unlockedCount = list.filter((item) => item.unlocked).length;
        return (
            <section className={styles.section}>
                {renderSectionTitle(
                    '选择章节',
                    unlockedCount > 0 ? `已解锁 ${unlockedCount}/${list.length}` : null,
                    '每篇只能单章解锁一次',
                )}
                {chapters === null || (chapterLoading && list.length === 0) ? (
                    <div className={styles.note}>
                        <Spin size="small" /> 正在读取章节价格…
                    </div>
                ) : list.length === 0 ? (
                    <div className={styles.note}>本篇暂无可单独解锁的章节，请选择全篇解锁</div>
                ) : (
                    <div className={styles.chips}>
                        {list.map((item) => {
                            const disabled = item.unlocked || item.unlockable === false;
                            const active = chapter === item.chapterTitle;
                            const tip = item.unlocked
                                ? '已解锁'
                                : item.unlockable === false
                                  ? item.reason || '该章暂不支持单独解锁'
                                  : `${item.contentDesc || '正文'} · ${item.sectionCount || 0} 小节`;
                            return (
                                <Tooltip key={item.chapterTitle} title={tip} zIndex={layer + 10}>
                                    <span className={styles.chipWrap}>
                                        <button
                                            type="button"
                                            disabled={disabled}
                                            aria-pressed={active}
                                            className={cx(styles.chip, {
                                                [styles.chipActive]: active && !disabled,
                                                [styles.chipDone]: item.unlocked,
                                            })}
                                            onClick={() => setChapter(item.chapterTitle)}
                                        >
                                            {item.unlocked ? <CheckCircleFilled /> : <LockOutlined />}
                                            <span className={styles.chipTitle}>
                                                {item.chapterTitle}
                                            </span>
                                            {!item.unlocked && item.price != null ? (
                                                <span className={styles.chipPrice}>
                                                    ¥{formatYuan(item.price)}
                                                </span>
                                            ) : null}
                                        </button>
                                    </span>
                                </Tooltip>
                            );
                        })}
                    </div>
                )}
                {blocked ? (
                    <div className={styles.note}>
                        当前订单暂不能单章解锁{reasons.length ? `：${reasons.join('；')}` : ''}
                    </div>
                ) : null}
                <div className={styles.summary}>
                    {chapterInfo ? (
                        <div className={styles.row}>
                            <span className={styles.rowMain}>
                                <span className={styles.rowTitle}>{chapterInfo.chapterTitle}</span>
                                <span className={styles.rowDesc}>
                                    {chapterInfo.unlockable === false && !chapterInfo.unlocked
                                        ? chapterInfo.reason || '该章暂不支持单独解锁'
                                        : `${chapterInfo.contentDesc || '正文'} · ${
                                              chapterInfo.sectionCount || 0
                                          } 小节`}
                                </span>
                            </span>
                            <b>¥{formatYuan(chapterInfo.price)}</b>
                        </div>
                    ) : (
                        <div className={styles.row}>
                            <span>请选择要解锁的章节</span>
                            <b className={styles.muted}>--</b>
                        </div>
                    )}
                    {renderDiscountRow()}
                    <div className={styles.total}>
                        <span>合计</span>
                        <b>{chapterInfo ? `¥${formatYuan(discountTotal(chapterInfo.price))}` : '--'}</b>
                    </div>
                </div>
            </section>
        );
    };

    const renderUpgradePlan = () => (
        <section className={styles.section}>
            {renderSectionTitle('价格明细')}
            <div className={styles.summary}>
                <div className={styles.row}>
                    <span>已单章解锁，补齐全文差价</span>
                    <b>¥{formatYuan(baseAmount)}</b>
                </div>
                {renderDiscountRow()}
                <div className={styles.total}>
                    <span>应付</span>
                    <b>¥{formatYuan(amount)}</b>
                </div>
            </div>
        </section>
    );

    const renderBenefits = () => (
        <ul className={styles.benefits} aria-label="解锁权益">
            {(BENEFITS[plan] || BENEFITS.full).map((text) => (
                <li key={text}>
                    <CheckCircleFilled />
                    <span>{text}</span>
                </li>
            ))}
        </ul>
    );

    // ---------- 右栏 / 底栏：付款 ----------

    const renderQr = () => {
        if (waitingChapter) {
            return (
                <div className={styles.qrEmpty}>
                    <LockOutlined />
                    <span>请先选择要解锁的章节</span>
                </div>
            );
        }
        if (prepareError) {
            return (
                <div className={styles.qrEmpty}>
                    <span>{prepareError}</span>
                    <Button
                        size="small"
                        icon={<ReloadOutlined />}
                        onClick={() => setPrepareSeq((n) => n + 1)}
                    >
                        重新获取付款码
                    </Button>
                </div>
            );
        }
        const loading = { loading: true };
        return (
            <PayQRCodes
                alipay={busy ? loading : channels.alipay}
                wechat={busy ? loading : channels.wechat}
                onRetry={retry}
                size={size}
                className={styles.payCodes}
            />
        );
    };

    const redeemHint =
        plan === 'full'
            ? '可使用毕业论文 / 开题报告 / 文献综述套餐码、金额兑换码或优惠券；优惠券面额不够时抵扣部分金额'
            : '单章解锁、补差价支持金额兑换码或优惠券';

    // 兑换码输入框直接展示，不用先点开；挂了优惠券就显示抵扣行和撤回
    const renderRedeem = () => (
        <div className={styles.redeem}>
            <div className={styles.redeemTitle}>
                <GiftOutlined />
                <span>有兑换码 / 套餐码 / 优惠券？</span>
            </div>
            {activeDiscount ? (
                <div className={styles.redeemApplied} role="status">
                    <span>
                        已用优惠券 <b>{activeDiscount.code}</b> 抵扣 ¥{formatYuan(activeDiscount.discountAmount)}，还需支付 ¥
                        {formatYuan(activeDiscount.payableAmount)}
                    </span>
                    <Button size="small" loading={redeeming} onClick={() => handleRevoke(activeDiscount.code, false)}>
                        撤回
                    </Button>
                </div>
            ) : (
            <div className={styles.redeemPanel}>
                <div className={styles.redeemRow}>
                    <Input
                        value={code}
                        onChange={(e) => setCode(e.target.value)}
                        onPressEnter={handleRedeem}
                        placeholder="输入兑换码或套餐码"
                        allowClear
                        maxLength={64}
                        aria-label="兑换码或套餐码"
                    />
                    <Button
                        type="primary"
                        loading={redeeming}
                        disabled={!payInfo?.orderNo || busy}
                        onClick={handleRedeem}
                    >
                        兑换
                    </Button>
                </div>
                {selectedPackage?.redeemCode && plan === 'full' ? (
                    <button
                        type="button"
                        className={styles.packageChip}
                        onClick={() => setCode(selectedPackage.redeemCode)}
                    >
                        使用已选套餐：{selectedPackage.redeemName || selectedPackage.redeemCode}
                    </button>
                ) : null}
                {revokePrompt ? (
                    <div className={styles.redeemPrompt} role="alert">
                        <span>
                            这张优惠券已用于订单 <b>{revokePrompt.previousOrderNo}</b>（尚未支付），撤回后才能用于本单
                        </span>
                        <div className={styles.redeemPromptActions}>
                            <Button type="primary" size="small" loading={redeeming} onClick={() => handleRevoke(revokePrompt.code, true)}>
                                撤回并用于本单
                            </Button>
                            <Button size="small" onClick={() => setRevokePrompt(null)}>
                                取消
                            </Button>
                        </div>
                    </div>
                ) : null}
                <div className={styles.redeemHint}>{redeemHint}</div>
            </div>
            )}
        </div>
    );

    const renderOrderNo = () =>
        payInfo?.orderNo ? (
            <div className={styles.orderNo}>
                <span className={styles.orderNoLabel}>订单号</span>
                <span className={styles.orderNoValue}>{payInfo.orderNo}</span>
                <button
                    type="button"
                    className={styles.copyBtn}
                    aria-label="复制订单号"
                    onClick={() => copyText(payInfo.orderNo, message)}
                >
                    <CopyOutlined />
                </button>
            </div>
        ) : null;

    const renderAgreement = () => (
        <span className={styles.agree}>
            付款即表示同意
            <a href="/agreement" target="_blank" rel="noreferrer">
                《用户协议》
            </a>
        </span>
    );

    const renderBusy = () =>
        busy && !waitingChapter ? (
            <div className={styles.busy} role="status">
                <LoadingOutlined /> {recreating || pendingAddons ? '正在更新订单…' : '正在生成付款码…'}
            </div>
        ) : null;

    /** 限时特惠：划线原价、已省金额、赠送的图表、倒计时 */
    const renderPromo = () =>
        activeOffer ? (
            <div className={cx(styles.promo, styles.promoOffer)} role="status">
                <span className={styles.promoTag}>
                    <FireFilled /> 限时 {offerPercent} 折
                </span>
                <span className={styles.promoText}>
                    原价 <s>¥{formatYuan(quote?.totalPrice)}</s>，折后 ¥{formatYuan(activeOffer.price)}
                </span>
                <span className={styles.promoTime}>
                    <ClockCircleOutlined /> 剩余 <b>{offerCountdown}</b> 付款有效
                </span>
            </div>
        ) : promoActive ? (
            <div className={styles.promo} role="status">
                <span className={styles.promoTag}>
                    <FireFilled /> 限时特惠
                </span>
                <span className={styles.promoText}>
                    原价 <s>¥{formatYuan(originalBase)}</s>，已省 ¥{formatYuan(originalBase - Number(quote?.basePrice))}
                    {freeFigures + freeTables > 0 ? `，赠 ${freeFigures} 图 ${freeTables} 表` : ''}
                </span>
                {countdown ? (
                    <span className={styles.promoTime}>
                        <ClockCircleOutlined /> 距结束 <b>{countdown}</b>
                    </span>
                ) : null}
            </div>
        ) : null;

    const renderPayColumn = (inline = false) => (
        <aside className={cx(styles.pay, inline && styles.payInline)}>
            {renderPromo()}
            <div className={styles.amount}>
                <span className={styles.amountLabel}>应付金额</span>
                <b className={styles.amountValue}>{amountText}</b>
                <span className={styles.amountNote}>{amountNote()}</span>
            </div>
            {renderQr()}
            <div className={styles.secure}>
                <SafetyOutlined />
                支付宝、微信扫任一码均可，到账后自动跳转
            </div>
            {renderBusy()}
            {renderRedeem()}
            {renderOrderNo()}
        </aside>
    );

    const renderSheetFooter = () => (
        <div className={styles.sheetFoot}>
            {renderPromo()}
            <div className={styles.sheetAmount}>
                <span className={styles.amountLabel}>应付</span>
                <b className={styles.amountValue}>{amountText}</b>
                <span className={styles.amountNote}>{amountNote()}</span>
            </div>
            {renderRedeem()}
            {renderBusy()}
            <div className={styles.sheetPay}>{renderQr()}</div>
            <div className={styles.sheetMeta}>
                {renderOrderNo()}
                {renderAgreement()}
            </div>
        </div>
    );

    const renderFooter = () => (
        <div className={styles.foot}>
            <span className={styles.footSafe}>
                <SafetyOutlined />
                安全支付
            </span>
            <span className={styles.footNote}>
                付款后请保存订单号，可在顶部「订单查询」查看进度、预览和下载 Word
            </span>
            {renderAgreement()}
        </div>
    );

    // 手机浏览器只有一个支付宝按钮，连同金额吸底；窄屏电脑仍是双码，放进内容流里免得吸底区太高
    const stickyPay = isMobile && isAlipayRedirect();

    const body = (
        <div className={cx(styles.dialog, isMobile && styles.dialogMobile)} data-plan={plan}>
            {!isMobile ? (
                <div className={styles.head}>
                    <h2 className={styles.title}>{dialogTitle}</h2>
                    <p className={styles.subtitle}>{subtitle}</p>
                </div>
            ) : null}
            <div className={styles.body}>
                <div className={styles.main}>
                    {!isUpgrade ? renderPlanTabs() : null}
                    {plan === 'full' ? (
                        <>
                            {renderAddons()}
                            {renderFullSummary()}
                        </>
                    ) : null}
                    {plan === 'single' ? renderSinglePlan() : null}
                    {plan === 'upgrade' ? renderUpgradePlan() : null}
                    {renderBenefits()}
                </div>
                {!isMobile ? renderPayColumn() : null}
                {isMobile && !stickyPay ? renderPayColumn(true) : null}
            </div>
            {stickyPay ? renderSheetFooter() : null}
            {isMobile && !stickyPay ? (
                <div className={styles.sheetAgree}>{renderAgreement()}</div>
            ) : null}
            {!isMobile ? renderFooter() : null}
        </div>
    );

    // 关弹窗挽留：限时 88 折（领取后 5 分钟内付款有效）
    const offerPreview = Math.round(Number(quote?.totalPrice || 0) * 0.88 * 100) / 100;
    const retentionModal = (
        <Modal
            open={open && retention}
            onCancel={handleClose}
            footer={null}
            width={440}
            centered
            zIndex={layer + 20}
            getContainer={portalContainer}
            className={styles.retention}
            maskClosable={false}
            destroyOnClose
        >
            <div className={styles.retentionHead}>
                <GiftOutlined /> 再省一点，轻松继续
            </div>
            <h2 className={styles.retentionTitle}>觉得稍微有点贵？</h2>
            <p className={styles.retentionDesc}>为你准备了一份限时优惠，让好内容更近一步。</p>
            <div className={styles.retentionCard}>
                <div className={styles.retentionLeft}>
                    <span className={styles.retentionLabel}>专属限时折扣 · 折后价</span>
                    <b className={styles.retentionPrice}>¥{formatYuan(offerPreview)}</b>
                    <span className={styles.retentionNote}>
                        原价 ¥{formatYuan(quote?.totalPrice)} · 领取后 5 分钟内付款有效
                    </span>
                </div>
                <div className={styles.retentionRight}>
                    <b className={styles.retentionOff}>
                        88<small>折</small>
                    </b>
                    <span className={styles.retentionNote}>限时优惠</span>
                </div>
            </div>
            <div className={styles.retentionTips}>
                <p className={styles.retentionTipsTitle}>温馨提示</p>
                <ol>
                    <li>领取后开始 5 分钟倒计时，超时未付款自动恢复原价</li>
                    <li>也可以返回修改大纲，去掉一些图表 / 公式 / 代码来降低费用</li>
                </ol>
            </div>
            <div className={styles.retentionActions}>
                <Button size="large" onClick={handleClose}>
                    暂不需要
                </Button>
                <Button
                    size="large"
                    type="primary"
                    loading={claiming}
                    onClick={handleClaimOffer}
                    className={styles.retentionGo}
                >
                    立即享受 88 折 →
                </Button>
            </div>
        </Modal>
    );

    if (isMobile) {
        return (
            <>
                <Drawer
                    open={open}
                    onClose={handleClose}
                    placement="bottom"
                    height="auto"
                    zIndex={layer}
                    maskClosable={false}
                    getContainer={portalContainer}
                    rootClassName={styles.sheet}
                    title={
                        <div className={styles.sheetTitle}>
                            <span>{dialogTitle}</span>
                            <span className={styles.sheetSubtitle}>{subtitle}</span>
                        </div>
                    }
                >
                    {body}
                </Drawer>
                {retentionModal}
            </>
        );
    }

    return (
        <>
            <Modal
                open={open}
                onCancel={handleClose}
                footer={null}
                width={isUpgrade ? 640 : 860}
                centered
                zIndex={layer}
                getContainer={portalContainer}
                className={styles.modal}
                maskClosable={false}
            >
                {body}
            </Modal>
            {retentionModal}
        </>
    );
}
